import { useQuery } from '@tanstack/react-query';
import { putSellBuy } from 'src/Pages/Event/api';
import { PutSellBuyResponse } from 'src/types/api';
import { PaymentType } from 'src/types/dto';
import { useCartStore } from 'src/Pages/Event/stores';

/**
 * Hook que busca as opções de parcelamento (installments) para um pagamento com cartão de crédito
 * baseado no valor atual do carrinho de um determinado evento e no sellId atual.
 * 
 * Retorna também o número máximo de parcelas disponíveis.
 */
export const useInstallments = ({
    sellId,
    eventID,
    referral,
}: {
    sellId?: string | number;
    eventID?: number | string;
    referral?: string;
}) => {
    const { getEventCartTotal } = useCartStore();
    /**
   * Calcula o valor total do carrinho com base no eventID.
   * Usa fallback 0 caso o ID seja inválido ou o carrinho esteja vazio.
   */
    const amount = eventID ? getEventCartTotal(String(eventID)) || 0 : 0;
    /**
   * Realiza a consulta à API simulando uma venda via cartão de crédito
   * para obter as opções de parcelamento disponíveis com base no valor atual do carrinho.
   */
    const query = useQuery<PutSellBuyResponse>({
    queryKey: ['installments', sellId, amount], // cache segmentado por sellId e valor
    queryFn: async () => {
        if (!sellId) return { installments: [] }; // fallback seguro

        return await putSellBuy({
        sellId: Number(sellId),
        payload: { paymentType: PaymentType.CreditCard }, 
        params: { ref: referral ?? '' },// fallback para string vazia
        });
    },
    enabled: !!sellId && amount > 0, // evita chamadas desnecessárias
    });
    /**
   * Extrai o número máximo de parcelas do array retornado pela API.
   * Se não houver parcelas, assume valor padrão de 12.
   */
    // Calcular máximo de parcelas
    const maxInstallments = query.data?.installments?.length
    ? Math.max(...query.data.installments.map(inst => inst.installment))
    : 12; // valor padrão
    
    // Retorna os dados da query e o número máximo de parcelas calculado
    return {
    ...query,
    maxInstallments,
    };
};
